import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowRight, Building2, Check, Landmark, Rocket, Building } from 'lucide-react';
import { AUDIENCES } from '@/data/site';
import { SectionHeader } from './SectionHeader';
import { ServiceMark } from './AmdenMark';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { cn } from '@/lib/utils';

const AUDIENCE_ICONS = {
  smes: Building2,
  established: Landmark,
  'larger-organisations': Building,
  startups: Rocket,
};

const PanelContent = ({ audience, testid }) => (
  <div data-testid={testid}>
    <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold-500">Who we work with</p>
    <h3 className="mt-4 text-2xl font-extrabold tracking-tight text-white sm:text-3xl">
      {audience.heading}
    </h3>
    <p className="mt-4 text-base leading-relaxed text-slate-300">{audience.description}</p>
    <ul className="mt-6 space-y-3">
      {audience.points.map((point) => (
        <li key={point} className="flex items-start gap-3 text-sm font-medium text-slate-200 sm:text-base">
          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gold-600/20">
            <Check className="h-3 w-3 text-gold-500" aria-hidden="true" />
          </span>
          {point}
        </li>
      ))}
    </ul>
    <Link
      to={audience.cta.href}
      data-testid="audience-panel-cta"
      className="group mt-8 inline-flex items-center gap-2 text-sm font-bold text-gold-500 transition-colors hover:text-gold-200"
    >
      {audience.cta.label}
      <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
    </Link>
  </div>
);

export const Audiences = () => {
  const [activeId, setActiveId] = useState(AUDIENCES[0].id);
  const reduce = useReducedMotion();
  const active = AUDIENCES.find((a) => a.id === activeId);

  return (
    <section aria-labelledby="audiences-heading" data-testid="audiences-section" className="bg-mist py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeader
          index="05"
          eyebrow="Who we work with"
          title="We support businesses at every stage"
          description="From ambitious startups to established enterprises, we provide tailored support that meets you where you are and helps you get where you want to be."
          testid="audiences-header"
        />

        {/* Desktop / tablet: selector + navy panel */}
        <div className="mt-14 hidden gap-8 lg:grid lg:grid-cols-12">
          <div className="space-y-3 lg:col-span-5" role="tablist" aria-label="Business audiences">
            {AUDIENCES.map((audience) => {
              const Icon = AUDIENCE_ICONS[audience.id];
              const isActive = audience.id === activeId;
              return (
                <button
                  key={audience.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  aria-controls="audience-panel"
                  data-testid={`audience-tab-${audience.id}`}
                  onClick={() => setActiveId(audience.id)}
                  onMouseEnter={() => setActiveId(audience.id)}
                  onFocus={() => setActiveId(audience.id)}
                  className={cn(
                    'relative w-full rounded-xl border bg-white p-5 text-left transition-[border-color,box-shadow] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-900 focus-visible:ring-offset-2',
                    isActive
                      ? 'border-navy-900 shadow-card'
                      : 'border-slate-200 hover:border-navy-300'
                  )}
                >
                  <span
                    className={cn(
                      'absolute inset-y-3 left-0 w-1 rounded-full transition-colors',
                      isActive ? 'bg-gold-600' : 'bg-transparent'
                    )}
                    aria-hidden="true"
                  />
                  <span className="flex items-center gap-4 pl-2">
                    <span
                      className={cn(
                        'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg transition-colors',
                        isActive ? 'bg-navy-900 text-white' : 'bg-navy-50 text-navy-700'
                      )}
                    >
                      <Icon className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <span>
                      <span className="block text-base font-bold text-navy-900">{audience.label}</span>
                      <span className="mt-0.5 block text-sm text-slate-500">{audience.tagline}</span>
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          <div className="lg:col-span-7">
            <div
              id="audience-panel"
              role="tabpanel"
              data-testid="audience-content-panel"
              className="relative h-full overflow-hidden rounded-2xl bg-navy-900 p-8 sm:p-12"
            >
              <ServiceMark color="#ffffff" className="pointer-events-none absolute -bottom-10 -right-10 h-56 w-56 opacity-[0.06]" />
              <AnimatePresence mode="wait">
                <motion.div
                  key={active.id}
                  initial={reduce ? false : { opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={reduce ? undefined : { opacity: 0, y: -12 }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                >
                  <PanelContent audience={active} testid="audience-panel-content" />
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Mobile: accessible accordion */}
        <div className="mt-12 lg:hidden" data-testid="audience-accordion">
          <Accordion type="single" collapsible defaultValue={AUDIENCES[0].id}>
            {AUDIENCES.map((audience) => {
              const Icon = AUDIENCE_ICONS[audience.id];
              return (
                <AccordionItem
                  key={audience.id}
                  value={audience.id}
                  className="mb-3 overflow-hidden rounded-xl border border-slate-200 bg-white px-5"
                  data-testid={`audience-accordion-${audience.id}`}
                >
                  <AccordionTrigger className="py-5 text-left hover:no-underline">
                    <span className="flex items-center gap-3">
                      <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-navy-50 text-navy-700">
                        <Icon className="h-4 w-4" aria-hidden="true" />
                      </span>
                      <span className="text-base font-bold text-navy-900">{audience.label}</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="mb-2 rounded-xl bg-navy-900 p-6">
                      <PanelContent audience={audience} testid={`audience-panel-content-${audience.id}`} />
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>
      </div>
    </section>
  );
};
